import React from 'react';
import type { DiaryItem } from '../models/diary';
import type { ActivityRecord, DiaryRecord, MealRecord } from '../models/diaryRecord';

interface PlanAgendaCardProps {
  item: DiaryItem;
  record?: DiaryRecord;
  onEdit: () => void;
  compact?: boolean; // condensed view hides the suggested-meal detail
}

// Per-type accent colors (shared with the create buttons / DiaryItemCard palette).
const TYPE_COLOR: Record<DiaryItem['type'], string> = {
  MealItem: '#10b981',
  FitnessItem: '#38b2ac',
  SupplementItem: '#8b5cf6',
  MedicationItem: '#f97316',
};

const getFitnessIcon = (subtype: string): string => {
  switch ((subtype || '').toLowerCase()) {
    case 'cardio': return '❤️';
    case 'gym': return '🏋️‍♂️';
    case 'sport': return '⚽';
    case 'swimming': return '🏊‍♂️';
    case 'yoga': return '🧘‍♀️';
    case 'pilates': return '🤸‍♀️';
    case 'walking': return '🚶‍♂️';
    default: return '🏃‍♂️';
  }
};

const getIcon = (item: DiaryItem): string => {
  switch (item.type) {
    case 'MealItem': return '🍽️';
    case 'FitnessItem': return getFitnessIcon(item.subtype as string);
    case 'SupplementItem': return '💊';
    case 'MedicationItem': return '💉';
    default: return '•';
  }
};

const num = (v: unknown): number => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

// Small status chip: pending / done / skipped.
const StatusChip: React.FC<{ record?: DiaryRecord }> = ({ record }) => {
  if (!record) return null;
  if (record.status === 'YES') {
    return <span style={chipStyle('#dcfce7', '#15803d')}>✅ Hecho</span>;
  }
  return (
    <span style={chipStyle('#fee2e2', '#b91c1c')}>
      ❌ Saltado{record.statusReason ? ` · ${record.statusReason}` : ''}
    </span>
  );
};

const chipStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block',
  background: bg,
  color,
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 7px',
  borderRadius: 10,
  lineHeight: 1.4,
});

// Plan vs Real macro comparison for meal items.
const MealMacros: React.FC<{ item: DiaryItem; record?: DiaryRecord }> = ({ item, record }) => {
  const target = item.mealTarget;
  const real = record?.mealRecord;
  if (!target && !real) return null;

  // What the patient actually logged for this slot (their own title, or the
  // chosen suggested meal). Shown even when the slot was skipped so we can
  // surface "they ate X instead".
  const loggedName = (real?.food_title || record?.mealName || '').trim();
  const skipped = record?.status === 'NO';

  const row = (label: string, planVal: number, realVal: number | null, unit: string) => {
    const delta = realVal != null ? realVal - planVal : null;
    const deltaColor = delta == null ? '#64748b' : delta >= 0 ? '#15803d' : '#b91c1c';
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4, fontSize: 11 }}>
        <span style={{ color: '#64748b' }}>{label}</span>
        <span style={{ color: '#334155' }}>
          {planVal}{unit}
          {realVal != null && (
            <>
              {' → '}
              <strong style={{ color: deltaColor }}>{Math.round(realVal)}{unit}</strong>
            </>
          )}
        </span>
      </div>
    );
  };

  const pCal = num(target?.calories);
  const pPro = num(target?.protein);
  const pCar = num(target?.carbs);
  const pFat = num(target?.fat);

  return (
    <div style={{ marginTop: 6, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {loggedName && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 2, fontSize: 11 }}>
          <span style={{ flexShrink: 0, color: '#94a3b8' }}>{skipped ? 'En su lugar:' : 'Registró:'}</span>
          <span style={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🍴 {loggedName}
          </span>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>
        <span>Plan</span>
        {real && <span>Real</span>}
      </div>
      {row('Cal', pCal, real ? num(real.energy_kcal) : null, '')}
      {row('Prot', pPro, real ? num(real.protein_g) : null, 'g')}
      {row('Carb', pCar, real ? num(real.carbs_g) : null, 'g')}
      {row('Gras', pFat, real ? num(real.fat_g) : null, 'g')}
    </div>
  );
};

const INTENSITY_LABEL: Record<string, string> = {
  low: 'suave',
  mid: 'media',
  high: 'alta',
};

// Compact metrics line for a logged fitness activity.
const ActivityMetricsRow: React.FC<{ real: ActivityRecord }> = ({ real }) => {
  const intensity = INTENSITY_LABEL[(real.intensity || '').toLowerCase()] || real.intensity;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11, color: '#64748b' }}>
      {real.duration_min != null && <span>⏱ {real.duration_min} min</span>}
      {intensity && <span>💪 {intensity}</span>}
      {real.energy_kcal != null && <span>🔥 {Math.round(num(real.energy_kcal))} kcal</span>}
    </div>
  );
};

// Compact macros line for a logged meal (used by off-plan cards, no plan to compare).
const MealMetricsRow: React.FC<{ real: MealRecord }> = ({ real }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11, color: '#64748b' }}>
    <span>🔥 {Math.round(num(real.energy_kcal))} kcal</span>
    <span>P {Math.round(num(real.protein_g))}g</span>
    <span>C {Math.round(num(real.carbs_g))}g</span>
    <span>G {Math.round(num(real.fat_g))}g</span>
  </div>
);

// What the patient actually logged for a fitness slot. The API exposes the
// first ActivityRecord of the diary record (mirroring mealRecord).
const ActivitySummary: React.FC<{ record?: DiaryRecord }> = ({ record }) => {
  const real = record?.activityRecord;
  if (!real) return null;
  const skipped = record?.status === 'NO';
  const loggedName = (real.title || real.activity_type_name || record?.activityName || '').trim();

  return (
    <div style={{ marginTop: 6, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 3 }}>
      {loggedName && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, fontSize: 11 }}>
          <span style={{ flexShrink: 0, color: '#94a3b8' }}>{skipped ? 'En su lugar:' : 'Registró:'}</span>
          <span style={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {loggedName}
          </span>
        </div>
      )}
      <ActivityMetricsRow real={real} />
    </div>
  );
};

// Supplement / medication dosage notes (the `description` field holds the dosage).
const DosageList: React.FC<{ item: DiaryItem }> = ({ item }) => {
  const entries =
    item.type === 'SupplementItem'
      ? (item.supplements || []).map(s => ({ name: s.supplementCatalog?.name, description: s.description }))
      : item.type === 'MedicationItem'
        ? (item.medications || []).map(m => ({ name: m.medicationCatalog?.name, description: m.description }))
        : [];

  const visible = entries.filter(e => e.name || e.description);
  if (visible.length === 0) return null;

  return (
    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {visible.map((e, i) => (
        <div key={i} style={{ padding: '5px 7px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
          {e.name && <div style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{e.name}</div>}
          {e.description && (
            <div style={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.35, whiteSpace: 'pre-wrap' }}>{e.description}</div>
          )}
        </div>
      ))}
    </div>
  );
};

// Suggested meals the patient can pick from for this meal slot.
const SuggestedMeals: React.FC<{ item: DiaryItem }> = ({ item }) => {
  const meals = (item.meals || []).filter(m => m && m.name);
  if (meals.length === 0) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, marginBottom: 3 }}>Sugerencias</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {meals.map(m => (
          <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 6, padding: '4px 7px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #dcfce7' }}>
            <span style={{ fontSize: 11.5, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
            {m.calories ? <span style={{ fontSize: 11, color: '#16a34a', flexShrink: 0 }}>{Math.round(num(m.calories))} kcal</span> : null}
          </div>
        ))}
      </div>
    </div>
  );
};

const PlanAgendaCard: React.FC<PlanAgendaCardProps> = ({ item, record, onEdit, compact = false }) => {
  const accent = TYPE_COLOR[item.type] || '#38b2ac';
  const isMeal = item.type === 'MealItem';
  const hasDosage = item.type === 'SupplementItem' || item.type === 'MedicationItem';

  return (
    <div
      onClick={onEdit}
      title="Editar"
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8,
        padding: '8px 10px',
        cursor: 'pointer',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.10)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ fontSize: 18, lineHeight: 1.2 }}>{getIcon(item)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.name}
          </div>
          {item.timeOfDay && (
            <div style={{ fontSize: 11, color: accent, fontWeight: 600 }}>🕐 {item.timeOfDay}</div>
          )}
        </div>
      </div>
      {record && (
        <div style={{ marginTop: 6 }}>
          <StatusChip record={record} />
        </div>
      )}
      {isMeal && <MealMacros item={item} record={record} />}
      {isMeal && !compact && <SuggestedMeals item={item} />}
      {item.type === 'FitnessItem' && <ActivitySummary record={record} />}
      {hasDosage && <DosageList item={item} />}
    </div>
  );
};

// Accent / icon / fallback label per record-level type (off-plan records carry
// the type on the record itself, there is no DiaryItem to derive it from).
const RECORD_TYPE: Record<string, { icon: string; color: string; label: string }> = {
  MEAL: { icon: '🍽️', color: TYPE_COLOR.MealItem, label: 'Comida libre' },
  FITNESS: { icon: '🏃‍♂️', color: TYPE_COLOR.FitnessItem, label: 'Actividad libre' },
  SUPPLEMENT: { icon: '💊', color: TYPE_COLOR.SupplementItem, label: 'Suplemento' },
  MEDICATION: { icon: '💉', color: TYPE_COLOR.MedicationItem, label: 'Medicamento' },
};

// Time an off-plan record should slot into within the day column.
export const recordTimeOfDay = (record: DiaryRecord): string =>
  record.timeOfDay || (record.date.split(' ')[1] || '').slice(0, 5);

// Card for a record the patient logged outside their plan (no DiaryItem).
// Visually distinct from plan cards: dashed border + "Fuera del plan" chip.
export const OffPlanRecordCard: React.FC<{ record: DiaryRecord }> = ({ record }) => {
  const meta = RECORD_TYPE[record.type] || { icon: '•', color: '#64748b', label: 'Registro' };
  const title = (
    record.activityRecord?.title ||
    record.mealRecord?.food_title ||
    record.mealName ||
    record.activityName ||
    meta.label
  ).trim();
  const time = recordTimeOfDay(record);

  return (
    <div
      style={{
        background: '#fbfdfe',
        border: '1px dashed #94a3b8',
        borderLeft: `3px solid ${meta.color}`,
        borderRadius: 8,
        padding: '8px 10px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ fontSize: 18, lineHeight: 1.2 }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </div>
          {time && (
            <div style={{ fontSize: 11, color: meta.color, fontWeight: 600 }}>🕐 {time}</div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 6 }}>
        <span style={chipStyle('#fef3c7', '#b45309')}>＋ Fuera del plan</span>
      </div>
      {(record.mealRecord || record.activityRecord) && (
        <div style={{ marginTop: 6, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
          {record.mealRecord && <MealMetricsRow real={record.mealRecord} />}
          {record.activityRecord && <ActivityMetricsRow real={record.activityRecord} />}
        </div>
      )}
    </div>
  );
};

export default PlanAgendaCard;
